import React, { useRef, useMemo } from 'react';
import { Box, Text, Accordion } from '@mantine/core';
import { ShadcnDashboard } from './ShadcnDashboard';
import { useQuadrantItems } from '../../hooks/useQuadrantItems';
import { LoadingState } from '../common/LoadingState';
import { useEntityEntitlements } from '../../hooks/useEntityEntitlements';
import { isAdministrator } from '../../types/EntityEntitlements';
import { debugVerboseLog, isDebugLoggingEnabled } from '../../utils/debugLog';
import { JsonViewer } from '../common/JsonViewer';
import {
    getKnownEntityTypeUsageCounts,
    getKnownEntityTypeUsageSnapshot,
    getUnexpectedEntityTypeUsageCounts,
    getUnexpectedEntityTypeUsageSnapshot,
    subscribeEntityTypeUsage,
} from '../../utils/navigationIconUtils';

interface DashboardProps {
    onNavigate: (
        destination: string,
        isEntity?: boolean,
        category?: 'person' | 'locality' | 'other' | 'meta' | 'utilities' | 'blacklist' | 'admin'
    ) => void;
    onNavigateHash?: (hash: string) => void;
    authResolved: boolean;
    active?: boolean;
}

export const Dashboard: React.FC<DashboardProps> = ({ onNavigate, onNavigateHash, authResolved, active = true }) => {
    const { quadrantItems, loading } = useQuadrantItems({ onNavigate, authResolved });
    const { entityEntitlements } = useEntityEntitlements();
    const hasLoadedOnce = useRef(false);
    const debugEnabled = isDebugLoggingEnabled();

    const isAdmin = isAdministrator(entityEntitlements);
    const hasEntitlements = entityEntitlements != null;
    const lastDebugKey = useRef<string>('');
    const debugKey = `${String(isAdmin)}:${String(hasEntitlements)}:${String(entityEntitlements?.IsAdministrator)}:${String(entityEntitlements?.Requestor?.Id)}`;
    if (debugKey !== lastDebugKey.current) {
        lastDebugKey.current = debugKey;
        debugVerboseLog('[Dashboard] isAdmin (from tier1 IsAdministrator)', {
            isAdmin,
            hasEntitlements,
            tier1IsAdministrator: entityEntitlements?.IsAdministrator,
            requestorId: entityEntitlements?.Requestor?.Id,
        });
    }


    const [usageTick, setUsageTick] = React.useState(0);
    React.useEffect(() => {
        if (!debugEnabled) return;
        return subscribeEntityTypeUsage(() => setUsageTick((t) => t + 1));
    }, [debugEnabled]);

    const knownEntityTypeUsage = useMemo(() => {
        if (!debugEnabled) return null;
        // usageTick ensures this recomputes as the dashboard renders and calls into icon utils.
        void usageTick;
        return getKnownEntityTypeUsageSnapshot();
    }, [debugEnabled, usageTick]);

    const knownEntityTypeUsageCounts = useMemo(() => {
        if (!debugEnabled) return null;
        void usageTick;
        return getKnownEntityTypeUsageCounts();
    }, [debugEnabled, usageTick]);

    const unexpectedEntityTypeUsage = useMemo(() => {
        if (!debugEnabled) return null;
        void usageTick;
        return getUnexpectedEntityTypeUsageSnapshot();
    }, [debugEnabled, usageTick]);

    const unexpectedEntityTypeUsageCounts = useMemo(() => {
        if (!debugEnabled) return null;
        void usageTick;
        return getUnexpectedEntityTypeUsageCounts();
    }, [debugEnabled, usageTick]);

    if (quadrantItems.length > 0) {
        hasLoadedOnce.current = true;
    }

    if (loading && !hasLoadedOnce.current) {
        return (
            <LoadingState variant="centered" size="lg" label="Loading dashboard…" minHeight="60vh" />
        );
    }

    return (
        <Box pos="relative">
            <ShadcnDashboard
                items={quadrantItems}
                onNavigateHash={onNavigateHash}
                onNavigate={onNavigate}
                active={active}
            />
            {debugEnabled && knownEntityTypeUsage && (
                <Box px={24} pb="md" pt="xs">
                    <Accordion defaultValue={null}>
                        <Accordion.Item value="known-entity-types">
                            <Accordion.Control>
                                <Text size="sm" fw={600} c="dimmed">
                                    Debug: KnownEntityType icon usage
                                </Text>
                            </Accordion.Control>
                            <Accordion.Panel>
                                <Accordion multiple defaultValue={[]}>
                                    {unexpectedEntityTypeUsage && Object.keys(unexpectedEntityTypeUsage).length > 0 && (
                                        <Accordion.Item value="unexpected">
                                            <Accordion.Control>
                                                <Text size="sm" fw={700} c="yellow.9">
                                                    Warning: unexpected entityType keys seen
                                                </Text>
                                            </Accordion.Control>
                                            <Accordion.Panel>
                                                <Box
                                                    p="sm"
                                                    style={{
                                                        border: '1px solid var(--mantine-color-yellow-5)',
                                                        borderRadius: 8,
                                                        background: 'color-mix(in srgb, var(--mantine-color-yellow-0) 60%, transparent)',
                                                    }}
                                                >
                                                    <Accordion multiple defaultValue={[]}>
                                                        {unexpectedEntityTypeUsageCounts && (
                                                            <Accordion.Item value="unexpected-counts">
                                                                <Accordion.Control>
                                                                    <Text size="sm" fw={600}>Unexpected keys (counts)</Text>
                                                                </Accordion.Control>
                                                                <Accordion.Panel>
                                                                    <JsonViewer
                                                                        data={unexpectedEntityTypeUsageCounts}
                                                                        description="Unique caller frames per unexpected entityType and per function."
                                                                    />
                                                                </Accordion.Panel>
                                                            </Accordion.Item>
                                                        )}
                                                        <Accordion.Item value="unexpected-callers">
                                                            <Accordion.Control>
                                                                <Text size="sm" fw={600}>Unexpected keys (callers)</Text>
                                                            </Accordion.Control>
                                                            <Accordion.Panel>
                                                                <JsonViewer data={unexpectedEntityTypeUsage} />
                                                            </Accordion.Panel>
                                                        </Accordion.Item>
                                                    </Accordion>
                                                </Box>
                                            </Accordion.Panel>
                                        </Accordion.Item>
                                    )}

                                    {knownEntityTypeUsageCounts && (
                                        <Accordion.Item value="known-counts">
                                            <Accordion.Control>
                                                <Text size="sm" fw={600}>Known types (counts)</Text>
                                            </Accordion.Control>
                                            <Accordion.Panel>
                                                <Text size="xs" c="dimmed" mb={6}>
                                                    Counts are unique caller stack frames per KnownEntityType (and per function).
                                                </Text>
                                                <JsonViewer data={knownEntityTypeUsageCounts} />
                                            </Accordion.Panel>
                                        </Accordion.Item>
                                    )}

                                    <Accordion.Item value="known-callers">
                                        <Accordion.Control>
                                            <Text size="sm" fw={600}>Known types (callers)</Text>
                                        </Accordion.Control>
                                        <Accordion.Panel>
                                            <JsonViewer data={knownEntityTypeUsage} />
                                        </Accordion.Panel>
                                    </Accordion.Item>
                                </Accordion>
                            </Accordion.Panel>
                        </Accordion.Item>
                    </Accordion>
                </Box>
            )}
        </Box>
    );
};
